// Piezas de los agentes y las rutinas que repiten varias pantallas: el detalle de un trabajo con su línea de tiempo,
// la decisión sobre una aprobación y el editor de horario.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Cargando, Modal } from '@glubi/ds';
import { api, mensajeDeError } from '../lib/api';
import { momento, usd } from '../lib/formato';
import { useSesion } from '../lib/sesion';
import { Area, Casilla, Estado, SoloSiAdministra } from './Comunes';

const ACTIVOS = ['en_cola', 'corriendo', 'esperando_aprobacion'];
const DISPARADOR: Record<string, string> = { manual: 'A mano', horario: 'Por horario', rutina: 'Paso de una rutina' };

export const invalidarTrabajo = (qc: ReturnType<typeof useQueryClient>) => {
  for (const k of ['tablero', 'agentes', 'rutinas', 'ejecucion']) void qc.invalidateQueries({ queryKey: [k] });
};

// Aprobar o rechazar una escritura. La nota es opcional y le llega al agente si se rechaza.
export function Decidir({ aprobacion, compacto }: { aprobacion: { id: string; herramienta: string }; compacto?: boolean }) {
  const { aviso } = useSesion();
  const qc = useQueryClient();
  const [nota, setNota] = useState('');
  const [rechazando, setRechazando] = useState(false);
  const decidir = useMutation({
    mutationFn: (decision: 'aprobar' | 'rechazar') => api(`/api/aprobaciones/${aprobacion.id}`, { decision, nota: nota.trim() || null }),
    onSuccess: (_r, decision) => { invalidarTrabajo(qc); aviso(decision === 'aprobar' ? 'Aprobado: el agente sigue' : 'Rechazado: el agente sabrá por qué'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  return (
    <SoloSiAdministra>
      <div className="stack" style={{ gap: 8 }}>
        {rechazando && <input className="field" placeholder="Por qué no (le llega al agente)" value={nota} onChange={(e) => setNota(e.target.value)} autoFocus />}
        <div className="fila">
          {!rechazando && <Button size={compacto ? 'sm' : 'md'} icon="check" disabled={decidir.isPending} onClick={() => decidir.mutate('aprobar')}>Aprobar</Button>}
          {rechazando
            ? <><Button size={compacto ? 'sm' : 'md'} variant="secondary" disabled={decidir.isPending} onClick={() => decidir.mutate('rechazar')}>Rechazar</Button>
              <Button size={compacto ? 'sm' : 'md'} variant="ghost" onClick={() => setRechazando(false)}>Volver</Button></>
            : <Button size={compacto ? 'sm' : 'md'} variant="ghost" icon="x" onClick={() => setRechazando(true)}>Rechazar</Button>}
        </div>
      </div>
    </SoloSiAdministra>
  );
}

function Bloque({ children, tono }: { children: React.ReactNode; tono?: 'error' | 'ok' }) {
  return (
    <div style={{
      background: tono === 'error' ? '#F7E6E4' : tono === 'ok' ? '#E6F1EA' : 'var(--surface-card)', color: tono === 'error' ? 'var(--state-error)' : 'var(--text-primary)',
      border: '1px solid var(--border-default)', borderRadius: 'var(--radius-md)', padding: '10px 14px', fontSize: 14, lineHeight: '21px', whiteSpace: 'pre-wrap', wordBreak: 'break-word',
    }}>{children}</div>
  );
}

// El detalle de un trabajo: qué se le pidió, cada vuelta del modelo, cada herramienta, cada aprobación y lo que costó.
export function DetalleEjecucion({ id, onClose }: { id: string; onClose: () => void }) {
  const { aviso, cuenta } = useSesion();
  const qc = useQueryClient();
  const [actual, setActual] = useState(id);
  const [correccion, setCorreccion] = useState('');
  const q = useQuery({
    queryKey: ['ejecucion', actual], queryFn: () => api<any>(`/api/ejecuciones/${actual}`),
    refetchInterval: (consulta) => (ACTIVOS.includes((consulta.state.data as any)?.estado) ? 1500 : false),
  });
  const cancelar = useMutation({
    mutationFn: () => api(`/api/ejecuciones/${actual}/cancelar`, {}),
    onSuccess: () => { invalidarTrabajo(qc); aviso('Trabajo cancelado'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });
  const corregir = useMutation({
    mutationFn: () => api(`/api/agentes/${q.data.agente_id}/aprendizajes`, { texto: correccion.trim(), ejecucionId: actual }),
    onSuccess: () => { setCorreccion(''); invalidarTrabajo(qc); aviso('Aprendizaje guardado: cuenta desde el próximo trabajo'); },
    onError: (e) => aviso(mensajeDeError(e)),
  });

  const e = q.data;
  const aprobacion = (llamadaId: string) => e?.aprobaciones.find((a: any) => a.llamada_id === llamadaId);
  return (
    <Modal title={e ? e.nombre : 'Trabajo'} width={760} onClose={onClose}
      subtitle={e && <span>{e.tipo === 'rutina' ? 'Rutina' : 'Agente'} · {DISPARADOR[e.disparador]} · {momento(e.creado_en)} · pedido por {e.pedida_por?.nombre}{e.version ? ` · versión ${e.version}` : ''}</span>}
      footer={<>
        {actual !== id && <Button variant="ghost" icon="chevron-left" onClick={() => setActual(id)}>Volver a la rutina</Button>}
        {e && ACTIVOS.includes(e.estado) && <SoloSiAdministra><Button variant="ghost" icon="x" disabled={cancelar.isPending} onClick={() => { if (confirm('¿Cancelar este trabajo? Lo que ya hizo queda hecho.')) cancelar.mutate(); }}>Cancelar trabajo</Button></SoloSiAdministra>}
        <Button onClick={onClose}>Cerrar</Button>
      </>}>
      {!e ? <Cargando /> : (
        <div className="stack" style={{ gap: 14 }}>
          <div className="entre">
            <Estado estado={e.estado} />
            <span className="muted" style={{ fontSize: 13 }}>Costo {usd(e.costo)}{e.terminada_en ? ` · terminó ${momento(e.terminada_en)}` : ''}</span>
          </div>
          {e.tipo === 'agente' && <div><span className="etiqueta">Encargo</span><Bloque>{e.encargo}</Bloque></div>}

          <div className="stack" style={{ gap: 8 }}>
            <span className="etiqueta">Lo que hizo</span>
            {!e.pasos.length && <span className="muted" style={{ fontSize: 13 }}>{e.estado === 'en_cola' ? 'Esperando turno…' : 'Todavía nada.'}</span>}
            {e.pasos.map((p: any, i: number) => {
              if (p.tipo === 'modelo') {
                // La última respuesta es el resultado: se muestra abajo una sola vez.
                const esResultado = i === e.pasos.length - 1 && e.estado === 'terminada' && p.texto === e.resultado;
                return p.texto && !esResultado ?<div key={i} className="stack" style={{ gap: 2 }}><Bloque>{p.texto}</Bloque><span className="muted" style={{ fontSize: 11 }}>{p.destino} · {usd(p.costo)}</span></div> : null;
              }
              if (p.tipo === 'herramienta') {
                return (
                  <div key={i} className={`paso ${p.error ? 'error' : ''}`}>
                    <b style={{ fontWeight: 500 }}>{p.nombre}</b> <code>{JSON.stringify(p.argumentos)}</code>
                    <div className="muted" style={{ marginTop: 4, whiteSpace: 'pre-wrap', maxHeight: 140, overflow: 'auto' }}>{p.resultado}</div>
                  </div>
                );
              }
              if (p.tipo === 'aprobacion') {
                const a = aprobacion(p.llamadaId);
                return (
                  <div key={i} className="card" style={{ padding: 12, borderColor: a?.estado === 'pendiente' ? 'var(--amber)' : undefined }}>
                    <div className="entre" style={{ marginBottom: 6 }}>
                      <span style={{ fontSize: 14 }}>Pidió permiso para escribir con <b style={{ fontWeight: 500 }}>{p.nombre}</b></span>
                      {a && <Estado estado={a.estado} />}
                    </div>
                    <pre className="codigo">{JSON.stringify(p.argumentos, null, 2)}</pre>
                    {a?.estado === 'pendiente' && e.estado === 'esperando_aprobacion' && <div style={{ marginTop: 10 }}><Decidir aprobacion={a} /></div>}
                    {a && a.estado !== 'pendiente' && a.decidida_por && <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>{a.decidida_por.nombre} · {momento(a.decidida_en)}{a.nota ? ` · «${a.nota}»` : ''}</div>}
                  </div>
                );
              }
              if (p.tipo === 'agente') {
                const h = e.hijos.find((x: any) => x.id === p.ejecucionId);
                return (
                  <button key={i} className="plantilla" onClick={() => setActual(p.ejecucionId)}>
                    <span className="entre" style={{ width: '100%' }}><b>Paso {p.paso + 1}: {p.agente}</b>{h && <Estado estado={h.estado} />}</span>
                    <span>{h?.resultado ?? h?.error ?? 'Ver lo que hizo'}</span>
                  </button>
                );
              }
              return <div key={i} className="muted" style={{ fontSize: 13 }}>{p.texto}</div>;
            })}
            {e.estado === 'corriendo' && <span className="typing"><span /><span /><span /></span>}
          </div>

          {e.estado === 'terminada' && e.resultado && <div><span className="etiqueta">Resultado</span><Bloque tono="ok">{e.resultado}</Bloque></div>}
          {e.error && <Bloque tono="error">{e.error}</Bloque>}

          {e.tipo === 'agente' && !ACTIVOS.includes(e.estado) && cuenta?.puedeAdministrar && (
            <div className="stack" style={{ gap: 8 }}>
              <Area label="Corrección para la próxima vez (queda como aprendizaje del negocio)" filas={2} value={correccion} onChange={setCorreccion}
                placeholder="Las facturas de Postobón se pagan a 30 días, no a 15." />
              <div><Button size="sm" variant="secondary" icon="book" disabled={correccion.trim().length < 3 || corregir.isPending} onClick={() => corregir.mutate()}>Guardar aprendizaje</Button></div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

const DIAS = [[1, 'Lun'], [2, 'Mar'], [3, 'Mié'], [4, 'Jue'], [5, 'Vie'], [6, 'Sáb'], [7, 'Dom']] as const;
export const horarioEnPalabras = (h: { hora: string; dias: number[] } | null) =>
  !h ? 'Solo a mano' : `${h.dias.length === 7 ? 'Todos los días' : h.dias.map((d) => DIAS[d - 1]![1]).join(', ')} · ${h.hora}`;

export function EditorHorario({ valor, onChange }: { valor: { hora: string; dias: number[] } | null; onChange: (v: { hora: string; dias: number[] } | null) => void }) {
  return (
    <div className="stack" style={{ gap: 8 }}>
      <span className="etiqueta">Cuándo trabaja solo (hora de Bogotá)</span>
      <Casilla checked={Boolean(valor)} onChange={(v) => onChange(v ? { hora: '07:00', dias: [1, 2, 3, 4, 5, 6] } : null)}>Por horario, además de cuando se le encargue</Casilla>
      {valor && (
        <div className="fila" style={{ gap: 12 }}>
          <input className="field" type="time" value={valor.hora} onChange={(e) => onChange({ ...valor, hora: e.target.value })} style={{ width: 120 }} aria-label="Hora" />
          {DIAS.map(([d, t]) => (
            <Casilla key={d} checked={valor.dias.includes(d)} onChange={(v) => onChange({ ...valor, dias: v ? [...valor.dias, d].sort() : valor.dias.filter((x) => x !== d) })}>{t}</Casilla>
          ))}
        </div>
      )}
    </div>
  );
}
